import { MouseEvent, ReactNode } from "react";

import { ButtonSize } from "@components/button/button-constants";
import Tooltip from "@components/tooltip/tooltip";
import { classNames } from "@helpers/css";
import { titleCase } from "@helpers/string";

import { ToggleButtonGroupTheme } from "./toggle-button-group-constants";

export type ToggleButtonGroupType<T> = {
  tooltip?: string;
  className?: string;
  title: string | ReactNode;
  active: boolean;
  disabled?: boolean;
  value?: T;
};

export const ToggleButtonGroup = <T,>({
  buttons,
  onClick,
  className = "",
  theme,
  size = ButtonSize.large,
  "aria-label": ariaLabel,
}: {
  className?: string;
  // Medium matches a medium Button's 24px, so a group can sit beside one;
  // anything else keeps the original 30px. Only the default theme reads it;
  // the rest carry their own dimensions.
  size?: ButtonSize;
  // Names the group for assistive tech.
  "aria-label"?: string;
  onClick: (
    button: ToggleButtonGroupType<T>,
    e: MouseEvent<HTMLButtonElement>,
  ) => void;
  buttons: ToggleButtonGroupType<T>[];
  theme: ToggleButtonGroupTheme;
}) => {
  const handleClick =
    (button: ToggleButtonGroupType<T>) =>
    (e: MouseEvent<HTMLButtonElement>) => {
      onClick(button, e);
    };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={classNames(
        "flex flex-wrap shrink-0",
        theme === ToggleButtonGroupTheme.roundedCompact &&
          "gap-0.5 p-0.5 rounded-full bg-black/10",
        theme === ToggleButtonGroupTheme.mergedLightBlueButtons &&
          "rounded-sm bg-white border divide-x",
        theme === ToggleButtonGroupTheme.sidebar && "gap-4",
        theme === ToggleButtonGroupTheme.separatedWhiteButtons && "gap-[10px]",
        theme === ToggleButtonGroupTheme.defaultButtons &&
          classNames(
            "gap-0 bg-gray-100 rounded-md ring-1 ring-gray-200",
            size === ButtonSize.medium ? "h-[24px]" : "h-[30px]",
          ),
        className,
      )}
    >
      {buttons.map((button) => (
        <Tooltip key={String(button.value)} text={button.tooltip}>
          <button
            type="button"
            className={classNames(
              "shrink-0 flex items-center",
              theme === ToggleButtonGroupTheme.roundedCompact &&
                "px-1.5 py-px tracking-tight rounded-full text-xs font-medium",
              theme === ToggleButtonGroupTheme.roundedCompact
                ? button.active
                  ? "bg-gray-50 text-gray-800"
                  : "hover:text-gray-800 text-gray-600"
                : null,
              theme === ToggleButtonGroupTheme.mergedLightBlueButtons &&
                "px-3 h-[30px] text-sm text-gray-600",
              theme === ToggleButtonGroupTheme.mergedLightBlueButtons
                ? button.active
                  ? "bg-blue-50 text-blue-800"
                  : "hover:text-gray-800 hover:bg-gray-50"
                : null,
              theme === ToggleButtonGroupTheme.sidebar &&
                "text-sm font-semibold px-1",
              theme === ToggleButtonGroupTheme.sidebar
                ? button.active
                  ? "text-gray-700"
                  : "text-gray-400 hover:text-gray-800"
                : null,
              theme === ToggleButtonGroupTheme.separatedWhiteButtons &&
                "text-gray-900 text-sm font-medium px-2 py-1 border rounded-md h-[24px]",
              theme === ToggleButtonGroupTheme.separatedWhiteButtons
                ? button.active
                  ? "bg-gray-100"
                  : "hover:bg-gray-50"
                : null,
              theme === ToggleButtonGroupTheme.defaultButtons &&
                classNames(
                  "rounded-md",
                  size === ButtonSize.medium
                    ? "px-2 h-[24px] text-sm"
                    : "px-4 h-[30px]",
                ),
              theme === ToggleButtonGroupTheme.defaultButtons
                ? button.active
                  ? "bg-white text-gray-800 ring-1 ring-gray-200"
                  : "text-gray-500 hover:text-gray-800"
                : null,
              button.className,
            )}
            disabled={button.disabled}
            onClick={handleClick(button)}
          >
            {typeof button.title === "string"
              ? titleCase(button.title)
              : button.title}
          </button>
        </Tooltip>
      ))}
    </div>
  );
};

export { ToggleButtonGroupTheme };
